/**
 * AIModuleNotice — unified EU AI Act compliance disclaimer shown under each AI module.
 *
 * Implements EU AI Act Article 50 (transparency obligation for AI systems interacting
 * with natural persons) + Article 52 (limited-risk classification disclosure).
 *
 * Usage:
 *   <AIModuleNotice />                          // default: educational
 *   <AIModuleNotice variant="medical-educational" /> // for health/risk modules
 *   <AIModuleNotice variant="clinical" />       // for Doctor Portal AI features
 *
 * Design goals:
 *   - Single source of truth — rename wording once, update everywhere
 *   - Minimal visual weight (small, muted, non-intrusive)
 *   - Always renders with the Brain icon + short microcopy
 */
import React from "react";
import { Brain } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";

const COPY = {
  educational: {
    bg: "Образователен инструмент с изкуствен интелект · Не замества медицинска консултация · Съответствие с Регламента за ИИ на ЕС (ограничен риск)",
    en: "AI-powered educational tool · Not a substitute for medical consultation · EU AI Act aligned (limited-risk)",
    es: "Herramienta educativa con IA · No sustituye la consulta medica · Alineado con la Ley de IA de la UE (riesgo limitado)",
  },
  "medical-educational": {
    bg: "AI помага да разберете данните си по-лесно. Финалните решения винаги остават при вас и вашия лекар.",
    en: "AI helps you understand your data more easily. Final decisions always stay with you and your physician.",
    es: "La IA te ayuda a entender tus datos más fácilmente. Las decisiones finales siempre las tomáis tú y tu médico.",
  },
  clinical: {
    bg: "Клиничен AI инструмент за подпомагане на решения · Не заменя клиничната преценка · Регламент за ИИ на ЕС · Всички изходи подлежат на проверка от специалист",
    en: "Clinical decision-support AI · Does not replace clinical judgment · EU AI Act · All outputs subject to specialist review",
    es: "IA de apoyo a la decision clinica · No reemplaza el juicio clinico · Ley de IA de la UE · Todas las salidas requieren revision por especialista",
  },
};

export default function AIModuleNotice({ variant = "educational", compact = false, className = "" }) {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const copy = (COPY[variant] || COPY.educational)[language] || COPY[variant].en;

  return (
    <p
      data-testid="ai-module-notice"
      data-variant={variant}
      className={`mt-3 flex items-start gap-2 text-[11px] leading-relaxed ${
        isDark ? "text-gray-500" : "text-gray-400"
      } ${compact ? "py-1" : "py-2"} ${className}`}
    >
      <Brain className="w-3 h-3 mt-0.5 flex-shrink-0 opacity-70" />
      <span>{copy}</span>
    </p>
  );
}
